import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subscription } from 'rxjs';
import { resolveSelfServiceApiUrl } from '../shared/self-service-api.utils';
import { copyRelationHops, isRelationHop, RelationDropdownConfig, RelationHop, RelationHopOption, RelationHopsConfig, relationPath, relationTemplate, sameRelationHop } from './relation-hops.types';

@Component({
  selector: 'app-dvm-relation-hops',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './relation-hops.component.html',
  styleUrl: './relation-hops.component.scss',
})
export class RelationHopsComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) config!: RelationHopsConfig;
  @Input() value: RelationHop[] = [];
  @Output() valueChange = new EventEmitter<RelationHop[]>();
  private readonly http = inject(HttpClient);
  private currentValue?: RelationHop[];
  private currentConfig?: RelationHopsConfig;
  private optionRequests = new Map<string, Subscription>();
  selections: Record<string, string> = {};
  options: Record<string, RelationHopOption[]> = {};
  loading: Record<string, boolean> = {};
  optionErrors: Record<string, string> = {};
  added: RelationHop[] = [];
  notice = '';

  ngOnChanges(): void {
    if (this.config === this.currentConfig && this.value === this.currentValue) return;
    this.cancelRequests();
    this.currentConfig = this.config;
    this.currentValue = this.value;
    this.added = copyRelationHops(this.value);
    this.notice = '';
    this.resetSelections();
  }

  private resetSelections(): void {
    this.selections = {};
    this.options = {};
    this.loading = {};
    this.optionErrors = {};
    for (const field of this.config.dropdowns) this.loadOptions(field);
  }

  dependenciesReady(field: RelationDropdownConfig): boolean {
    return (field.dependsOn ?? []).every((id) => !!this.selections[id]);
  }

  loadOptions(field: RelationDropdownConfig): void {
    this.optionRequests.get(field.id)?.unsubscribe();
    this.loading[field.id] = false;
    this.optionErrors[field.id] = '';
    this.options[field.id] = (field.options ?? []).flatMap((option) => {
      const candidate = option as RelationHopOption;
      if (isRelationHop(candidate.hop)) return [candidate];
      const simple = option as { label: string; value: string };
      return simple.label && simple.value ? [{ label: simple.label, value: simple.value, hop: {} as RelationHop }] : [];
    });
    const api = field.optionsApi;
    if (!api || !this.dependenciesReady(field)) return;
    this.loading[field.id] = true;
    const url = resolveSelfServiceApiUrl(String(relationTemplate(api.url, this.selections, true)));
    const request = this.http.request<unknown>(api.method ?? 'GET', url, {
      body: relationTemplate(api.body, this.selections),
    }).subscribe({
      next: (response) => {
        this.loading[field.id] = false;
        const rows = api.responsePath ? relationPath(response, api.responsePath) : response;
        if (!Array.isArray(rows)) {
          this.optionErrors[field.id] = `Unable to read ${field.label} options. Check the configured response path.`;
          return;
        }
        this.options[field.id] = rows.flatMap((row: unknown) => {
          const value = relationPath(row, api.valuePath);
          if (value == null || !['string', 'number'].includes(typeof value)) return [];
          const label = String(relationPath(row, api.labelPath) ?? value);
          // A `mapTo` dropdown only ever contributes its own field to the
          // combined hop (see combinedHop()), so each option just needs a
          // value/label pair — it doesn't need to resolve to a full hop.
          if (field.mapTo) return [{ value: String(value), label, hop: {} as RelationHop }];
          const rawHop = api.hopPath ? relationPath(row, api.hopPath) : row;
          const hop = api.hopFields ? {
            targetEntity: relationPath(rawHop, api.hopFields.targetEntity),
            searchAttribute: relationPath(rawHop, api.hopFields.searchAttribute),
            extractAttribute: relationPath(rawHop, api.hopFields.extractAttribute),
          } : rawHop;
          if (!isRelationHop(hop)) return [];
          return [{ value: String(value), label, hop }];
        });
        if (rows.length && !this.options[field.id].length) this.optionErrors[field.id] = `No valid ${field.label} relations. Check the configured field mappings.`;
      },
      error: () => {
        this.loading[field.id] = false;
        this.optionErrors[field.id] = `Unable to load ${field.label} options.`;
      },
    });
    this.optionRequests.set(field.id, request);
  }

  select(field: RelationDropdownConfig, value: string | null | undefined): void {
    this.notice = '';
    this.selections = { ...this.selections };
    if (value === null || value === undefined || value === '') delete this.selections[field.id];
    else this.selections[field.id] = value;
    const changed = new Set([field.id]);
    let expanded = true;
    while (expanded) {
      expanded = false;
      for (const dependent of this.config.dropdowns) {
        if (!changed.has(dependent.id) && dependent.dependsOn?.some((id) => changed.has(id))) {
          delete this.selections[dependent.id];
          changed.add(dependent.id);
          expanded = true;
        }
      }
    }
    for (const dependent of this.config.dropdowns) {
      if (dependent.id !== field.id && changed.has(dependent.id)) this.loadOptions(dependent);
    }
  }

  private selectedOption(field: RelationDropdownConfig): RelationHopOption | undefined {
    return this.options[field.id]?.find((option) => option.value === this.selections[field.id]);
  }

  get canAdd(): boolean {
    return this.config.dropdowns.some((field) => !!this.selectedOption(field)) && this.config.dropdowns.every((field) =>
      !this.loading[field.id] && !this.optionErrors[field.id] && (field.required === false || !!this.selectedOption(field)));
  }

  private combinedHop(): RelationHop | null {
    const mapped = this.config.dropdowns.filter((field) => field.mapTo);
    if (mapped.length) {
      const hop = {} as RelationHop;
      for (const field of mapped) {
        const option = this.selectedOption(field);
        if (!option) return null;
        hop[field.mapTo!] = option.value;
      }
      return isRelationHop(hop) ? hop : null;
    }
    return this.selectedOption(this.config.dropdowns[0])?.hop ?? null;
  }

  add(): void {
    if (!this.canAdd) return;
    const next = copyRelationHops(this.added);
    const hop = this.combinedHop();
    if (!hop) return;
    if (!next.some((existing) => sameRelationHop(existing, hop))) next.push(hop);
    const count = next.length - this.added.length;
    this.notice = count ? `${count} relation${count === 1 ? '' : 's'} added.` : 'These relations are already added.';
    if (!count) return;
    this.emit(next);
    this.resetSelections();
  }

  remove(index: number): void {
    this.notice = 'Relation removed.';
    this.emit(this.added.filter((_, current) => current !== index));
  }

  move(index: number, direction: -1 | 1): void {
    const target = index + direction;
    if (target < 0 || target >= this.added.length) return;
    const next = [...this.added];
    [next[index], next[target]] = [next[target], next[index]];
    this.emit(next);
  }

  labelFor(hop: RelationHop): string {
    for (const field of this.config.dropdowns) {
      if ((this.options[field.id] ?? []).some((option) => sameRelationHop(hop, option.hop))) return field.label;
    }
    return hop.targetEntity || 'Relation';
  }

  private emit(value: RelationHop[]): void {
    this.added = copyRelationHops(value);
    this.currentValue = this.added;
    this.valueChange.emit(this.added);
  }

  private cancelRequests(): void {
    this.optionRequests.forEach((request) => request.unsubscribe());
    this.optionRequests.clear();
  }
  ngOnDestroy(): void { this.cancelRequests(); }
}
